import Service from './Service';
import OpenAIApi from './server/OpenAIApi';
import Server from './server';
import ScriptLoader from './ScriptLoader';
import checkContentType from '../utils/checkContentType';
import { ANSWER, CATEGORY, SUB_TREE, TEXT } from '../models/nodetypes';
import Constants from '../submodules/logictry_config/constants';
import Conversation from '../models/conversation';
class OpenAIService extends Service {
  // Cache the text embeddings that have been received
  textEmbeddings = {};
  conversations = {};

  // Get Existing Conversation
  getConversation = async (app) => {
    if (!this.conversations[app]) {
      const response = await OpenAIApi.query({ app });
      if (response?._id) this.conversations[app] = new Conversation(response._id, response.ct, response.ut, response.a, response.m);
    }
    return this.conversations[app];
  }

  getSuggestions = async (html) => {
    this.sendRequest(`${Constants.logictryOrigin}/v1/openai`, 'POST', JSON.stringify({ action: 'GetContentSuggestions', html }), (response, complete) => {
      this.openAIState = { response, complete };
      this.suggestions = [];
      this.html = html;
      try {
        this.suggestions = parseSuggestions(this.openAIState.response);
      } catch {
        //
      }
      this.emitStateUpdate();
    });
  }

  // Create Content by a type and description
  createContent = (settings) => {
    const { type, description } = settings;
    if (this.openAIState && !this.openAIState.complete) return;
    this.openAIState = {
      type,
      description,
      response: '',
      complete: false,
    };
    this.suggestions = null;
    this.emitStateUpdate();
    this.sendRequest(`${Constants.logictryOrigin}/v1/openai`, 'POST', JSON.stringify({ action: 'CreateContent', ...(settings || {})}), (response, complete) => {
      response = trimAndRemoveFORMATTER(response || '');
      this.openAIState = {
        type,
        description,
        response,
        complete
      };
      this.emitStateUpdate();
    });
  }
  /** One request: server runs spreadsheet → form → directory AI + DB creates; streams progress text then JSON after DIRECTORY_PIPELINE_MARKER. */
  createDirectoryPipeline = (settings) => {
    const { title, description, color, company } = settings || {};
    if (this.openAIState && !this.openAIState.complete) return;
    this.openAIState = {
      type: 'directorypipeline',
      title,
      description,
      response: '',
      complete: false,
    };
    this.suggestions = null;
    this.emitStateUpdate();
    this.sendRequest(`${Constants.logictryOrigin}/v1/openai`, 'POST', JSON.stringify({
      action: 'CreateDirectoryPipeline',
      title,
      description,
      color,
      company,
    }), (response, complete) => {
      this.openAIState = {
        type: 'directorypipeline',
        title,
        description,
        response: response || '',
        complete,
      };
      this.emitStateUpdate();
    });
  }

  editTemplate = (settings) => {
    const { prompt, content, scope, nodeType } = settings || {};
    if (this.openAIState && !this.openAIState.complete) return;
    this.openAIState = {
      type: 'edittemplate',
      scope,
      prompt,
      response: '',
      complete: false,
    };
    this.suggestions = null;
    this.emitStateUpdate();
    this.sendRequest(`${Constants.logictryOrigin}/v1/openai`, 'POST', JSON.stringify({
      action: 'EditTemplate',
      prompt,
      content,
      scope,
      nodeType,
    }), (response, complete) => {
      this.openAIState = {
        type: 'edittemplate',
        scope,
        prompt,
        response: trimAndRemoveFORMATTER(response || ''),
        complete,
      };
      this.emitStateUpdate();
    });
  }

  // update embeddings for Apps and Collections
  updateEmbedding = async (app, collection) => {
    let add;
    if (app) {
      add = await __getAllAppChunks(app);
    } else if (collection) {
      add = __getAllCollectionChunks(collection);
    }
    if (!add || add.error) return add.error || { error: 'NotFound' }

    // Send texts
    return OpenAIApi.create({
      action: 'UpdateEmbeddings',
      add,
      app: app?._id,
      collection: collection?._id
    });
  }
  askQuestion = async (question, app, report, collection, messages) => {
    let ids; // ID(s) of APPs to perform a semantic match
    let query = question; // The query or question being asked
    let context;
    let conversation;
    let error;

    // Allow asking questions on collection or apps individually
    if (collection) {
      // Include the collectionId and appId(s)
      ids = [collection._id, ...collection.apps.map(({ _id }) => _id)];
    } else if (app) {
      // Include the SubAppIds
      const subtreeNodes = app.root.__recursivelyGetAllNodesByTypes(app.root, [SUB_TREE]);
      if (report) {
        // This is the called if the chatbot replaces the report
        ({ conversation, error } = this.__getReportConversationAsynchronousFromInitialPrompt(app, messages?.slice(0, -1)));
      } else if (subtreeNodes && subtreeNodes.length > 0) {
        // Use existing vectors in the database for all sub trees
        ids = [app._id, ...subtreeNodes.map(({ text }) => text)];
      } else {
        // This is called if you want to use dynamic ebeddings
        const start = Math.max(1, messages.length - 2 - 20);
        const end = messages.length - 2;
        ({ context, conversation } = await this.__conversationAboutBodyOfText(app, question, messages.slice(start, end)));
      }
    }
    if (error) {
      this.openAIState = {
        response: '',
        complete: true,
      };
      this.emitStateUpdate();
    }

    // Construct prompt
    this.openAIState = {
      response: '',
      complete: false,
    };
    this.emitStateUpdate();
    this.sendRequest(`${Constants.logictryOrigin}/v1/openai`, 'POST', JSON.stringify({ action: 'AskQuestion', query, ids, context, conversation, app: app?._id, collection: collection?._id, project: app?.project }), (response, complete) => {
      this.openAIState = {
        response,
        complete
      };
      this.emitStateUpdate();
    });
  }
  getReportAnswerSynchronousForStaticReport = async (tree) => {
    const result = await OpenAIApi.create({
      action: 'GetReportResult',
      _id: tree._id,
      answers: tree.getOpenAIPrompt()
    });
    return result;
  }

  // PRIVATE API CALLS
  // DO NOT CALL DIRECTLY BELOW
  __getReportConversationAsynchronousFromInitialPrompt = (tree, additionalConversation) => {
    const prompt = tree.getOpenAIPrompt();
    let conversation = [];
    Object.keys(prompt).forEach((assistant) => {
      conversation.push({ assistant });
      const user = prompt[assistant].join(' | ');
      conversation.push({ user });
    });
    if (additionalConversation) conversation = [...conversation, ...additionalConversation];

    // Reverse array
    conversation.reverse();
    const reversedArray = [...conversation];
    conversation = [];
    reversedArray.forEach((message) => {
      if (isTokenLengthTooLong(JSON.stringify(conversation))) return;
      conversation.push(message);
    });
    conversation.reverse();
    if (conversation.length === 0) {
      return { error: 'EmptyConversation' };
    }
    return { conversation };
  }

  __conversationAboutBodyOfText = async (tree, query, conversation) => {
    const texts = await __getAllAppChunks(tree);
    if (!texts) return {};

    // Format texts and get embeddings
    let context = texts.filter((v) => v);
    const mustBreakTheDocumentIntoChunks = isTokenLengthTooLong(JSON.stringify(context));

    // Use embeddings to split up a document if the document link exceeds the max token limit
    if (mustBreakTheDocumentIntoChunks) {
      const allTextEmbeddingsFound = context.every((text) => this.textEmbeddings[text]);
      const queryWithConversation = conversation.length > 0 && `Based upon the previous questions and answers\n\n${conversation.map(({ question, answer }) => question && `## Question: ${question}` || answer && `## Answer: ${answer}`).join('\n\n')}\n\nAnswer the following question: ${query}` || query;
      const chunks = allTextEmbeddingsFound ? [queryWithConversation] : [queryWithConversation, ...context];
      const { embeddings, error } = await OpenAIApi.create({
        action: 'GetEmbeddings',
        chunks,
      });

      if (error || !embeddings || !embeddings[0]) {
        this.openAIState = {
          error,
          complete: true
        };
        this.emitStateUpdate();
        return {};
      };

      // Get Embeddings
      const questionEmbedding = embeddings[0];
      if (embeddings.length > 1) {
        embeddings.slice(1).forEach((e, i) => {
          this.textEmbeddings[context[i]] = e;
        });
      }

      // Calculate similarities
      const similarities = [];
      context.forEach((formattedText, i) => {
        const embedding = this.textEmbeddings[formattedText];
        const similarity = calculateCosineSimilarity(questionEmbedding, embedding);
        similarities.push({ index: i, similarity, embedding, text: context[i] });
      });
      similarities.sort((a, b) => b.similarity - a.similarity);

      // Sort in ascending order of index
      context = [];
      similarities.forEach(({ text }) => {
        // Only check for previous similarity if i > 0
        if (isTokenLengthTooLong(JSON.stringify([...context, ...(conversation || [])]))) return {};
        context.push(text);
      });
    }

    // Construct prompt
    this.openAIState = {
      response: '',
      complete: false,
    };

    // Send Async Request
    return { context, conversation };
  }
  sendRequest(url, method, body, cb) {
    this.xhttp = new XMLHttpRequest();
    this.xhttp.onreadystatechange = () => {
      if (this.xhttp.readyState === XMLHttpRequest.LOADING || this.xhttp.readyState === XMLHttpRequest.DONE) {
        cb(this.xhttp.responseText, this.xhttp.readyState === XMLHttpRequest.DONE);
      }
    };
    this.xhttp.open(method, url, true);
    this.xhttp.setRequestHeader("Content-Type", "application/json;charset=UTF-8");
    const csrfToken = document.querySelector('meta[name="csrf-token"]');
    if (csrfToken) this.xhttp.setRequestHeader("csrf-token", csrfToken.content);
    if (Constants.isIphone) {
      this.xhttp.setRequestHeader("platform", 'ios');
      const session = Server.getSessionTokens();
      if (session) {
        this.xhttp.setRequestHeader("access-token", session.accessToken);
        this.xhttp.setRequestHeader("session-token", session.sessionToken);
      }
    }
    this.xhttp.send(body);
  }
  cancelRequests() {
    this.xhttp?.abort();
  }
}

function trimAndRemoveFORMATTER(text) {
  return removeSurroundingQuotes(text.replaceAll('```json', '').replaceAll('```html', '').replaceAll('```plaintext', '').replaceAll('```', '').trim());
}
function removeSurroundingQuotes(str) {
  if (str.startsWith('"') && str.endsWith('"')) {
    return str.slice(1, -1);
  } else if (str.startsWith('"')) {
    return str.slice(1);
  } else if (str.endsWith('"')) {
    return str.slice(0, -1);
  }
  return str;
}
function calculateCosineSimilarity(vector1, vector2) {
  let dotProduct = 0;
  let normVector1 = 0;
  let normVector2 = 0;
  for (let i = 0; i < vector1.length; i += 1) {
    dotProduct += vector1[i] * vector2[i];
    normVector1 += vector1[i] * vector1[i];
    normVector2 += vector2[i] * vector2[i];
  }
  normVector1 = Math.sqrt(normVector1);
  normVector2 = Math.sqrt(normVector2);
  return dotProduct / (normVector1 * normVector2);
}

/** Intake form template title: base directory title + " Intake Form" (no duplicate suffix). */
export function intakeFormContentTitle(directoryTitle) {
  const t = (directoryTitle || '').trim();
  if (!t) return 'Intake Form';
  if (/intake form$/i.test(t)) return t;
  return `${t} Intake Form`;
}

/** Replace the first [Title] line in Logictry tree text. */
export function replaceTreeTitleLine(treeText, newTitle) {
  if (!treeText || newTitle == null || newTitle === '') return treeText;
  return treeText.replace(/^\[Title\]\s*.*/m, `[Title] ${newTitle}`);
}

export function extractCleanText(htmlString) {
  if (!htmlString || typeof htmlString !== 'string') return '';
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  // Remove script/style/nav/etc
  const skipTags = ['SCRIPT', 'STYLE', 'NAV', 'FOOTER', 'HEADER', 'NOSCRIPT', 'ASIDE'];
  skipTags.forEach(tag => {
    doc.querySelectorAll(tag).forEach(el => el.remove());
  });

  // Define meaningful block-level tags that warrant spacing
  const blockTags = new Set([
    'P', 'DIV', 'SECTION', 'ARTICLE',
    'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
    'LI', 'UL', 'OL', 'TABLE', 'TR',
    'BLOCKQUOTE', 'PRE', 'HR', 'BR'
  ]);

  let text = '';

  function walk(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const content = node.textContent.replace(/\s+/g, ' ').trim();
      if (content) text += content + ' ';
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      if (blockTags.has(node.tagName)) text += '\n';
      for (const child of node.childNodes) walk(child);
      if (blockTags.has(node.tagName)) text += '\n';
    }
  }

  walk(doc.body);

  // Collapse excess whitespace and trim
  return text.replace(/\s*\n\s*/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
}

async function __getAllAppChunks(tree) {
  if (tree.customSource && tree.customSource.url) {
    try {
      const { googleUrl, googleDriveUrl, youtubeUrl, loomUrl, airtableUrl, googleMapsUrl, canvaUrl, googleCalendarUrl, vimeoUrl, instagramEmbedCode, tiktokEmbedCode, isLogictryUrl } = checkContentType(tree.customSource.url);
      if (googleUrl || googleDriveUrl || youtubeUrl || loomUrl || airtableUrl || googleMapsUrl || canvaUrl || googleCalendarUrl || vimeoUrl || instagramEmbedCode || tiktokEmbedCode || isLogictryUrl) return;
      const filePath = `${Constants.logictryOrigin}/links?url=${tree.customSource.url}`;
      const response = await fetch(filePath);
      if (!response.ok) throw new Error('Failed to fetch file');
      const arrayBuffer = await response.arrayBuffer();
      const contentType = await Server.getContentType(tree.customSource.url);
      if (contentType === 'application/pdf') {
        return extractPdfChunks(arrayBuffer);
      } else {
        //
      }
    } catch {
      //
    }
  } else {
    // const categoryNodes = app.root.__recursivelyGetAllNodesByTypes(app.root, [CATEGORY]);
    const textNodes = tree.root.__recursivelyGetAllNodesByTypes(tree.root, [TEXT, ANSWER]);
    const tooLong = [];
    textNodes.forEach(({ text }) => {
      if (isTokenLengthTooLong(text)) {
        tooLong.push(text);
      }
    });
    if (tooLong.length > 0) return { error: 'TextTooLong' };

    //
    let tableOfContents = ['Table of Contents'];
    const textNodesWithTOC = [];

    const tocCategoryAlreadyIncluded = new Set();
    let tocIndex = [];

    if (textNodes) {
      textNodes.forEach(({ text, parents }) => {
        let textNodeWithTOC = [];
        let level = 0;
        parents.forEach((parent) => {
          if (parent.isOneOfTypes([CATEGORY])) {
            const tocString = tocIndex.slice(0, level + 1).join('.') + ' ' + extractCleanText(parent.text);
            textNodeWithTOC.push(tocString);
            if (!tocCategoryAlreadyIncluded.has(parent)) {
              tocCategoryAlreadyIncluded.add(parent);
              tocIndex[level] = (tocIndex[level] || 0) + 1;
              tocIndex = tocIndex.slice(0, level + 1);
              if (!tableOfContents.includes(tocString)) tableOfContents.push(tocString);
            }
            level += 1;
          }
        });

        // Final content line
        textNodeWithTOC.push(extractCleanText(text));
        textNodesWithTOC.push(textNodeWithTOC.join('\n'));
      });
    }
    return [tableOfContents.join('\n'), ...textNodesWithTOC].filter((v) => v);
  }
}
function __getAllCollectionChunks() {

}
function parseSuggestions(text) {
  const suggestions = [];
  const regex = /## SUGGESTION (\d+) ##([\s\S]*?)(?:## END SUGGESTION \1 ##|$)/g;
  let match;

  while ((match = regex.exec(text)) !== null) {
    const content = match[2].trim();
    suggestions.push(content);
  }

  return suggestions;
}

async function extractPdfChunks(arrayBuffer) {

  const pdfData = new Uint8Array(arrayBuffer);
  const pdf = await window.pdfjsLib.getDocument({ data: pdfData }).promise;
  const numPages = pdf.numPages;

  const lines = [];
  let fullText = "";

  // STEP 1: Extract lines
  for (let pageNumber = 1; pageNumber <= numPages; pageNumber++) {
    const page = await pdf.getPage(pageNumber);
    const textContent = await page.getTextContent();

    let lastY = null;
    let line = "";
    let fontSize = 12;
    textContent.items.forEach((item) => {
      const y = item.transform[5];
      const size = item.transform[0];
      if (lastY !== null && Math.abs(lastY - y) > 3) {
        if (line.trim()) {
          lines.push({ text: line.trim(), fontSize });
          fullText += line.trim() + "\n";
        }
        line = "";
      }
      line += item.str + " ";
      fontSize = size;
      lastY = y;
    });

    if (line.trim()) {
      lines.push({ text: line.trim(), fontSize });
      fullText += line.trim() + "\n";
    }
  }

  // STEP 2: Small PDF → return whole
  if (!isEmbeddingLengthTooLong(fullText)) {
    return [fullText.trim()];
  }

  // STEP 3: Detect body font size
  const sizeCount = {};

  lines.forEach(l => {
    sizeCount[l.fontSize] = (sizeCount[l.fontSize] || 0) + 1;
  });

  const bodyFontSize = parseFloat(
    Object.entries(sizeCount).sort((a,b)=>b[1]-a[1])[0][0]
  );

  // STEP 4: Build sections
  const sections = [];
  let currentSection = { title: "", text: "" };
  lines.forEach(line => {
    if (line.fontSize > bodyFontSize * 1.2) {
      if (currentSection.text.trim()) {
        sections.push(currentSection);
      }
      currentSection = {
        title: line.text,
        text: ""
      };
    } else {
      currentSection.text += line.text + "\n";
    }
  });

  if (currentSection.text.trim()) {
    sections.push(currentSection);
  }

  // STEP 5: Section chunking
  let chunks = [];
  sections.forEach(section => {
    const combined = section.title + "\n" + section.text;
    if (!isEmbeddingLengthTooLong(combined)) {
      chunks.push(combined.trim());
      return;
    }

    const paragraphs = section.text.split(/\n+/);
    let chunk = section.title + "\n";
    paragraphs.forEach(p => {
      if (!p.trim()) return;
      if (isEmbeddingLengthTooLong(chunk + p)) {
        chunks.push(chunk.trim());
        chunk = section.title + "\n" + p + "\n";
      } else {
        chunk += p + "\n";
      }
    });

    if (chunk.trim()) {
      chunks.push(chunk.trim());
    }
  });

  // STEP 6: Final guaranteed safety split
  const safeChunks = [];
  chunks.forEach((chunk) => {
    if (!isEmbeddingLengthTooLong(chunk)) {
      safeChunks.push(chunk);
      return;
    }

    const sentences = chunk.split(/(?<=[.!?])\s+/);
    let current = "";
    sentences.forEach(sentence => {
      if (!sentence.trim()) return;
      if (isEmbeddingLengthTooLong(current + sentence)) {
        if (current.trim()) safeChunks.push(current.trim());
        current = sentence + " ";
      } else {
        current += sentence + " ";
      }
    });
    if (current.trim()) {
      safeChunks.push(current.trim());
    }
  });

  // STEP 7: HARD FINAL SPLIT (guarantee)
  const finalChunks = [];
  safeChunks.forEach((chunk) => {
    if (!isEmbeddingLengthTooLong(chunk)) {
      finalChunks.push(chunk);
      return;
    }
    const maxChars = 24000;
    for (let i = 0; i < chunk.length; i += maxChars) {
      finalChunks.push(chunk.slice(i, i + maxChars));
    }
  });

  return finalChunks;

}

function estimateTokens(text) {
  return Math.ceil(text.length / 3.5);
}

function isEmbeddingLengthTooLong(text) {
  return estimateTokens(text) > 6500;
}

function isTokenLengthTooLong(text, maxTokens = 100000) {
  return estimateTokens(text) > maxTokens;
}

const singleton = new OpenAIService();
ScriptLoader.onStateUpdate(() => {
  if (!ScriptLoader.isLoaded(Constants.PDFJS)) return;
  if (!window.pdfjsLib) return;
  window.pdfjsLib.disableWorker = true;
  window.pdfjsLib.GlobalWorkerOptions.workerSrc = Constants.PDFWorker;
});
ScriptLoader.load(Constants.PDFJS);
export default singleton;
